import ReactPlaceholder from 'react-placeholder'
import { TableRow, TableCell, Table, TableBody, Icon } from 'semantic-ui-react'
import React from 'react'

import { FixtureSamplingHistoryQuery } from '../../../rpcs/FixtureSamplingHistoryQuery'
import { Column } from '@120wateraudit/envirio-components'
import { facilityGrid_facilityGrid_items as FixtureGridItem } from '../../../data-access/__generated__/types'
import { fixtureSamplingHistory_fixtureSamplingHistory as FixtureHistoryItem } from '../../../data-access/__generated__/types'
import styles from './AccordionRow.module.scss'
import { column } from '../FacilityGrid'

interface RowState {
  isTableAccordionOpen: boolean
}

interface RowProps {
  fixtureResponse: FixtureGridItem
  columns: Column[]
  platform: 'desktop' | 'mobile'
  accountId: number
}

export class AccordionRow extends React.Component<RowProps, RowState> {
  constructor(props: any) {
    super(props)

    this.state = {
      isTableAccordionOpen: false
    }
  }

  render() {
    const { platform } = this.props

    return platform === 'desktop'
      ? this.renderDesktopTableRow()
      : platform === 'mobile'
        ? this.renderMobileTableRow()
        : null
  }

  private renderDesktopTableRow = (): JSX.Element => {
    const { isTableAccordionOpen } = this.state
    const { accountId } = this.props
    const {
      fixtureId,
      fixtureCode,
      fixtureType,
      resultsPerFixture,
      fixtureLocation,
      leadResult,
      remediationAction,
      collectedOn,
      leadLevelLimit
    } = this.props.fixtureResponse

    return (
      <>
        <TableRow
          key={fixtureId}
          className={
            isTableAccordionOpen
              ? styles.activeRow + ' parentTable'
              : 'parentTable'
          }
          onClick={() => this.toggleTable(resultsPerFixture)}
        >
          <TableCell>{fixtureCode}</TableCell>
          <TableCell>{!fixtureType ? '--' : fixtureType}</TableCell>
          <TableCell>{!fixtureLocation ? '--' : fixtureLocation}</TableCell>
          {this.resultCellBuilder(leadResult, leadLevelLimit, 'ppb')}
          <TableCell>{!remediationAction ? '--' : remediationAction}</TableCell>
          <TableCell>{formatCollectedOn(collectedOn)}</TableCell>
          <ToggleCell
            showToggle={resultsPerFixture > 1}
            onClick={() => this.toggleTable(resultsPerFixture)}
            isOpen={isTableAccordionOpen}
          />
        </TableRow>

        {isTableAccordionOpen && (
          <TableRow
            key={fixtureId + '-accordion'}
            className={styles.accordionTableBody}
          >
            <Table.Cell colSpan={7}>
              <h4>Fixture Sampling History</h4>

              <FixtureSamplingHistoryQuery variables={{ fixtureId, accountId }}>
                {({ data, loading, error }) => {
                  if (!data || !data.fixtureSamplingHistory || error) {
                    return null
                  }

                  return (
                    <ReactPlaceholder type="text" ready={!loading}>
                      <Table className={styles.subTable}>
                        <TableBody>
                          <Table.Row className={styles.tableHeader}>
                            {this.props.columns.map((c, i) =>
                              this.renderColumnHeader(c, i)
                            )}
                          </Table.Row>
                          {data.fixtureSamplingHistory.map(
                            (sampleRow: any, sampleIndex: number) => (
                              <TableRow
                                className={styles.tableRow}
                                key={sampleIndex}
                              >
                                <TableCell>{fixtureCode}</TableCell>
                                <TableCell>
                                  {!sampleRow.fixtureType
                                    ? '--'
                                    : sampleRow.fixtureType}
                                </TableCell>
                                <TableCell>
                                  {!sampleRow.fixtureLocation
                                    ? '--'
                                    : sampleRow.fixtureLocation}
                                </TableCell>
                                {this.resultCellBuilder(
                                  sampleRow.resultValue,
                                  sampleRow.sampleLimit,
                                  'ppb'
                                )}
                                <TableCell>
                                  {!sampleRow.remediationAction
                                    ? '--'
                                    : sampleRow.remediationAction}
                                </TableCell>
                                <TableCell>
                                  {formatCollectedOn(sampleRow.collectedOn)}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </ReactPlaceholder>
                  )
                }}
              </FixtureSamplingHistoryQuery>
            </Table.Cell>
          </TableRow>
        )}
      </>
    )
  }

  private renderMobileTableRow = (): JSX.Element => {
    const { isTableAccordionOpen } = this.state
    const { accountId } = this.props
    const {
      fixtureId,
      fixtureCode,
      fixtureType,
      resultsPerFixture,
      fixtureLocation,
      leadResult,
      leadLevelLimit
    } = this.props.fixtureResponse

    return (
      <>
        <TableRow
          key={fixtureId}
          className={isTableAccordionOpen ? styles.activeRow : ''}
          onClick={() => this.toggleTable(resultsPerFixture)}
        >
          {leadResult > leadLevelLimit ? (
            <TableCell style={{ textAlign: 'center' }}>
              <Icon className={styles.redDot} name="circle" />
            </TableCell>
          ) : (
            <TableCell />
          )}
          <TableCell>{fixtureCode}</TableCell>
          <TableCell>{!fixtureType ? '--' : fixtureType}</TableCell>
          <TableCell>{!fixtureLocation ? '--' : fixtureLocation}</TableCell>
          <ToggleCell
            showToggle
            onClick={() => this.toggleTable(resultsPerFixture)}
            isOpen={isTableAccordionOpen}
          />
        </TableRow>

        {isTableAccordionOpen && (
          <TableRow
            key={fixtureId + '-accordion'}
            className={styles.accordionTableBody}
          >
            <Table.Cell colSpan={7}>
              <h4>Fixture Sampling History</h4>

              <FixtureSamplingHistoryQuery variables={{ fixtureId, accountId }}>
                {({ data, loading, error }) => {
                  if (!data || !data.fixtureSamplingHistory || error) {
                    return null
                  }

                  const mobileAccordionColumns = [
                    column('Result', 'result'),
                    column('Remediation Action', 'remediationAction'),
                    column('Collected On', 'collectedOn')
                  ]

                  return (
                    <ReactPlaceholder type="text" ready={!loading}>
                      <Table className={styles.subTable} unstackable>
                        <TableBody>
                          <Table.Row className={styles.tableHeader}>
                            {mobileAccordionColumns.map((c, i) =>
                              this.renderColumnHeader(c, i)
                            )}
                          </Table.Row>
                          {data.fixtureSamplingHistory.map(
                            (
                              sampleRow: FixtureHistoryItem | null,
                              sampleIndex: number
                            ) => {
                              if (!sampleRow) {
                                return
                              }

                              return (
                                <TableRow
                                  className={styles.tableRow}
                                  key={sampleIndex}
                                >
                                  {this.resultCellBuilder(
                                    sampleRow.resultValue,
                                    sampleRow.sampleLimit,
                                    'ppb'
                                  )}
                                  <TableCell>
                                    {!sampleRow.remediationAction
                                      ? '--'
                                      : sampleRow.remediationAction}
                                  </TableCell>
                                  <TableCell>
                                    {!sampleRow.collectedOn
                                      ? '--'
                                      : formatCollectedOn(
                                          sampleRow.collectedOn
                                        )}
                                  </TableCell>
                                </TableRow>
                              )
                            }
                          )}
                        </TableBody>
                      </Table>
                    </ReactPlaceholder>
                  )
                }}
              </FixtureSamplingHistoryQuery>
            </Table.Cell>
          </TableRow>
        )}
      </>
    )
  }

  // Typed number but the body has always handled null; the mobile row now relies on that.
  private resultCellBuilder = (
    result: number | null | undefined,
    resultLimit: number,
    unitOfMeasure: string
  ): JSX.Element => {
    return (
      <TableCell
        className={
          result != null && result > resultLimit ? styles.exceedance : ''
        }
      >
        {result === undefined || result === null
          ? '--'
          : Number.isInteger(result)
            ? result + ' ' + unitOfMeasure
            : result.toFixed(2) + ' ' + unitOfMeasure}
      </TableCell>
    )
  }

  private renderColumnHeader = (columnHeader: Column, columnIndex: number) => {
    return (
      <Table.HeaderCell
        key={`table-headercell-${columnHeader.key}-${columnIndex}`}
      >
        <div className={styles.headerCellContainer}>{columnHeader.name}</div>
      </Table.HeaderCell>
    )
  }

  private toggleTable = (resultsPerFixture: number) => {
    const { platform } = this.props

    if (resultsPerFixture > 1 || platform === 'mobile') {
      this.setState({ isTableAccordionOpen: !this.state.isTableAccordionOpen })
    }
  }
}

interface ToggleCellProps {
  showToggle: boolean
  onClick(): void
  isOpen: boolean
}

const ToggleCell = ({ showToggle, onClick, isOpen }: ToggleCellProps) => {
  if (!showToggle) {
    return <TableCell />
  }
  return (
    <TableCell>
      {
        <Icon
          className={styles.tableIcon}
          onClick={onClick}
          name={!isOpen ? 'plus square' : 'minus square'}
        />
      }
    </TableCell>
  )
}

const formatCollectedOn = (collectedOn: string | number | null): string => {
  if (typeof collectedOn === 'string') {
    if (stringContainsOnlyNumbers(collectedOn)) {
      return new Date(Number(collectedOn)).toLocaleDateString()
    } else {
      return new Date(collectedOn).toLocaleDateString()
    }
  }
  return '--'
}

const stringContainsOnlyNumbers = (s: string) => /^\d+$/.test(s)
